import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote } from "lucide-react";
import axios from "axios";
import { LEGACY_QUOTES } from "./legacyQuotes";

/* Hero visual — Marathi inspirational quote cards on family legacy / vanshavali.
 * Path deliberately uses `/hero-quotes/` (fresh Cloudflare cache-key) with `?v=` cache-bust
 * to avoid the stale-edge issue that hit the older `/hero-tree/` slot. Cards live at
 * /app/frontend/public/hero-quotes/01.webp … 08.webp (~900KB total, 80% WebP quality). */
const ASSET_V = "20261005h";
const IMAGE_SLIDES = Array.from({ length: 8 }, (_, i) => ({
    type: "image",
    src: `/hero-quotes/${String(i + 1).padStart(2, "0")}.webp?v=${ASSET_V}`,
    alt: `Family legacy quote ${i + 1}`,
}));
const TEXT_SLIDES = LEGACY_QUOTES.map((text) => ({ type: "quote", text }));
// Interleave image + text slides so the carousel alternates visuals and quotes.
const QUOTE_SLIDES = (() => {
    const out = [];
    const n = Math.max(IMAGE_SLIDES.length, TEXT_SLIDES.length);
    for (let i = 0; i < n; i++) {
        if (i < IMAGE_SLIDES.length) out.push(IMAGE_SLIDES[i]);
        if (i < TEXT_SLIDES.length) out.push(TEXT_SLIDES[i]);
    }
    return out;
})();

export function useHomepageContent() {
    const [data, setData] = useState({ hero_tree: [], benefit_slides: [], story_audio: [] });
    useEffect(() => {
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/homepage`)
            .then((r) => setData(r.data || {}))
            .catch(() => {});
    }, []);
    return data;
}

export function HeroFamilyTree() {
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        const t = setInterval(() => setIdx((v) => (v + 1) % QUOTE_SLIDES.length), 5000);
        return () => clearInterval(t);
    }, []);

    return (
        <div className="lg:col-span-6 relative lg:-mt-10" data-testid="hero-family-tree">
            <div
                className="relative mx-auto lg:ml-auto lg:mr-0 w-full max-w-[580px] lg:max-w-[660px] aspect-[3/2] rounded-[26px] overflow-hidden shadow-[0_24px_60px_rgba(11,58,140,0.28)] bg-gradient-to-br from-[#0B3A8C] to-[#1554B0] border-[6px] border-white ring-1 ring-black/5"
                data-testid="hero-quote-carousel"
            >
                <AnimatePresence mode="wait">
                    {QUOTE_SLIDES[idx].type === "quote" ? (
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.5, ease: "easeInOut" }}
                            className="absolute inset-0 flex flex-col items-center justify-center text-center px-7 sm:px-12"
                            data-testid={`hero-quote-slide-${idx}`}
                        >
                            <Quote className="h-8 w-8 sm:h-10 sm:w-10 text-[#C9A55B] mb-3" />
                            <p className="text-white font-semibold leading-snug" style={{ fontSize: "clamp(15px, 2.1vw, 23px)" }}>
                                {QUOTE_SLIDES[idx].text}
                            </p>
                            <span className="mt-4 text-[11px] tracking-[0.18em] uppercase text-white/70">Family Repository</span>
                        </motion.div>
                    ) : (
                        <motion.img
                            key={idx}
                            src={QUOTE_SLIDES[idx].src}
                            alt={QUOTE_SLIDES[idx].alt}
                            initial={{ opacity: 0, scale: 1.02 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.98 }}
                            transition={{ duration: 0.55, ease: "easeInOut" }}
                            loading="eager"
                            className="absolute inset-0 h-full w-full object-cover"
                            data-testid={`hero-quote-slide-${idx}`}
                        />
                    )}
                </AnimatePresence>

                {/* Dots */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
                    {QUOTE_SLIDES.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            aria-label={`Show quote ${i + 1}`}
                            data-testid={`hero-quote-dot-${i}`}
                            onClick={() => setIdx(i)}
                            className={`h-1.5 rounded-full transition-all ${
                                i === idx ? "w-5 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"
                            }`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
